import { createElement, useCallback } from 'react';
import { defineMessages } from 'react-intl';

import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';

import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	resetPublishedSuggestions,
	startPublishedSuggestions,
} from './published/publishedSuggestionsStore.ts';
import { cancelLiveDocEditModeSelection } from './live/liveDocEditModeSelection.ts';
import { SuggestionsPanelLayout } from './components/SuggestionsPanelLayout.tsx';
import { SuggestionsCount } from './components/SuggestionsHeader.tsx';
import { SuggestionsHeaderActionGroup } from './components/SuggestionsHeaderActionGroup.tsx';

const i18n = defineMessages({
	suggestionsPanelHeaderLabel: {
		id: 'suggestions-panel.panel.header.review-suggestions.label',
		defaultMessage: 'Review suggestions',
		description:
			'The text is shown as the header label at the top of the AI suggestions sidebar panel, identifying the panel to the user.',
	},
});

type ShowSuggestionsPanelOptions = {
	entryPoint?: AiSuggestionsRightRailEntryPoint;
	/**
	 * Set when opening the panel on a published page. There is no editor to read suggestions from,
	 * so the panel lists the suggestions fetched for this content.
	 */
	publishedContentId?: string;
};

export const useShowSuggestionsPanel = (): {
	isSuggestionsPanelShown: boolean;
	showSuggestionsPanel: (options?: ShowSuggestionsPanelOptions) => void;
	hideSuggestionsPanel: (afterCloseCallback?: () => void) => Promise<void>;
} => {
	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar, changePanel } = useObjectSidebarActions();
	const { cloudId, isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();
	const [contentType = ContentType.PAGE] = useContentType();

	const showSuggestionsPanel = useCallback(
		({ entryPoint, publishedContentId }: ShowSuggestionsPanelOptions = {}): void => {
			if (!isAdminHubAIEnabled || !isAIEarlyAccessEnabled) {
				return;
			}

			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The suggested edits experiment is the kill switch for the view mode Suggestions panel subgate.
				publishedContentId &&
				expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) &&
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- View mode Suggestions panel subgate.
				fg('confluence_suggestions_panel_view_mode')
			) {
				startPublishedSuggestions(
					publishedContentId,
					`ari:cloud:confluence:${cloudId}:${contentType}/${publishedContentId}`,
				);
			}

			const suggestionsPanel = {
				id: PanelName.SuggestionsPanel,
				headerComponentElements: {
					headerLabel: i18n.suggestionsPanelHeaderLabel,
					HeaderAfterIconElement: SuggestionsCount,
					// Renders the more menu followed by the filter menu; the panel header appends its
					// close button after this element, so the filter button sits between the two.
					HeaderRightAlignedElement: SuggestionsHeaderActionGroup,
				},
				BodyComponent: () => createElement(SuggestionsPanelLayout, { entryPoint }),
				closeOptions: {
					onPanelClose: () => {
						resetPublishedSuggestions();
						cancelLiveDocEditModeSelection();
					},
				},
			};

			if (isObjectSidebarShown) {
				void changePanel(suggestionsPanel, 'push');
				return;
			}

			showObjectSidebar(suggestionsPanel, 'push');
		},
		[
			changePanel,
			cloudId,
			contentType,
			isAdminHubAIEnabled,
			isAIEarlyAccessEnabled,
			isObjectSidebarShown,
			showObjectSidebar,
		],
	);

	return {
		isSuggestionsPanelShown: isObjectSidebarShown && panel?.id === PanelName.SuggestionsPanel,
		showSuggestionsPanel,
		hideSuggestionsPanel: hideObjectSidebar,
	};
};
